import {
  BookingsRoutes,
  CoursesRoutes,
  RIDE_PRODUCTIONS_URL,
  RideRoutes,
} from "../enums/routes";
import { EntityPathKeys, entityPathMappings } from "../mappers";
import {
  AIRoleTypeEnum,
  AiStreamEventEnum,
  IAiStreamCallbacks,
  IChatHistoryItem,
  TAiStreamEvent,
  TAiUiElement,
} from "../models";

export const appendChunkToLastAssistantMessage = (
  prev: IChatHistoryItem[],
  text: string,
): IChatHistoryItem[] => {
  const updated = [...prev];
  const lastMsg = updated[updated.length - 1];
  if (lastMsg?.role === AIRoleTypeEnum.Assistant) {
    updated[updated.length - 1] = {
      ...lastMsg,
      text: lastMsg.text + text,
    };
  }
  return updated;
};

/**
 * Entities whose records do NOT live at the generic `/{path}/edit/{id}` CRUD route.
 *
 * Rides and bookings are mounted under a view-mode segment (`/rides/table/…`,
 * `/bookings/calendar/…` or the plain grid), so building their URLs from
 * `entityPathMappings` alone produces a route that does not exist.
 */
const ENTITY_ROUTE_OVERRIDES: Record<string, { list: string; edit?: string }> =
  {
    ride: {
      list: RideRoutes.SEARCH_GRID_MULTI_SELECT,
      edit: RideRoutes.EDIT_BASE_GRID_MULTI_SELECT,
    },
    booking: {
      list: BookingsRoutes.SEARCH_GRID,
      edit: BookingsRoutes.EDIT_BASE,
    },
    course: { list: CoursesRoutes.SEARCH_GRID },
    rideProduction: { list: RIDE_PRODUCTIONS_URL },
  };

/**
 * Resolve an assistant `navigate`/`entityRef` target to an in-app route.
 *
 * The assistant only ever names an entity and an id — it never emits a URL — so this is
 * where a destination first exists. Returns undefined for anything unmapped, and the
 * caller renders plain text instead of a dead link.
 */
export const resolveEntityRoute = (
  entity: string,
  id?: number,
): string | undefined => {
  const override = ENTITY_ROUTE_OVERRIDES[entity];
  if (override) {
    if (id === undefined) {
      return override.list;
    }
    return override.edit
      ? `${override.edit}${id}`
      : `${override.list}/edit/${id}`;
  }

  const segment = entityPathMappings[entity as EntityPathKeys];
  if (!segment) {
    return undefined;
  }
  return id === undefined ? `/${segment}` : `/${segment}/edit/${id}`;
};

/**
 * Absolute URL for opening an in-app route in a new tab.
 *
 * The admin app runs on a HASH router (`createHashRouter`), so the route has to go after
 * `#`. Passing the bare path to `window.open` would request it from the server, which
 * serves the SPA shell and lands the user on the home page instead.
 */
export const buildInAppHref = (route: string): string => {
  const { origin, pathname } = window.location;
  return `${origin}${pathname}#${route}`;
};

/**
 * Private URL scheme for an assistant entity mention.
 *
 * Mentions are rewritten into markdown links purely so the renderer's existing link
 * handling can pick them up; the scheme never reaches the browser.
 */
export const ENTITY_SCHEME = "ryde-entity:";

/** Markdown link syntax, used to strip any link the model wrote itself. */
const MARKDOWN_LINK = /\[([^\]]*)\]\([^)]*\)/g;

const escapeBrackets = (value: string): string =>
  value.replace(/[[\]]/g, match => `\\${match}`);

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Match a label against the answer text tolerating whitespace differences.
 *
 * The runtime collapses every whitespace run in a label to a single space before sending
 * it, so a mention the model wrapped across two lines ("Ride\n7") would never be found by
 * an exact search for the stored "Ride 7" — the chip would silently degrade to plain text.
 * Each run is matched as `\s+` instead, which also covers the reverse case.
 */
const findLabel = (
  source: string,
  label: string,
  from: number,
): { index: number; length: number } | undefined => {
  const tokens = label.split(/\s+/).filter(Boolean);
  // An all-whitespace label would compile to an empty pattern, which matches at every
  // offset and would wrap nothing in a link.
  if (!tokens.length) {
    return undefined;
  }
  const found = new RegExp(tokens.map(escapeRegExp).join("\\s+")).exec(
    source.slice(from),
  );
  return found
    ? { index: from + found.index, length: found[0].length }
    : undefined;
};

/**
 * Locate the id inside a matched mention, so the link can wrap the id and nothing else.
 *
 * Prefers the bracketed form the product writes — brackets included, so they stay attached
 * to the number under bidi isolation instead of drifting to the far side of a Hebrew
 * phrase. Falls back to a standalone number for a label written without them, and returns
 * undefined when the id is not in the text at all (the caller then links the whole label,
 * which is better than linking nothing).
 */
const findIdSpan = (
  matched: string,
  id: number,
): { index: number; length: number; text: string } | undefined => {
  // Spaces and tabs only, never a newline: the link TEXT must stay on one line to parse.
  const bracketed = new RegExp(`\\[[ \\t]*${id}[ \\t]*\\]`).exec(matched);
  if (bracketed) {
    return {
      index: bracketed.index,
      length: bracketed[0].length,
      text: `[${id}]`,
    };
  }

  // Bounded by non-digits so 7 does not match inside 71. Written with a capture group
  // rather than a lookbehind, which Safari only learned recently.
  const bare = new RegExp(`(^|\\D)(${id})(\\D|$)`).exec(matched);

  return bare
    ? {
        index: bare.index + bare[1].length,
        length: String(id).length,
        text: String(id),
      }
    : undefined;
};

/**
 * Turn the id in each mention the assistant registered into a tappable link.
 *
 * A text rewrite rather than a separate chip row, so the mention stays where it reads
 * naturally. Only the id is linked, not the whole "Name [ID]" phrase: the name is prose and
 * reads as prose, while the id is the part that identifies a record and is worth a click.
 * The bracketed run is what carries the bidi isolation, which is what stops `[1229]` from
 * jumping to the wrong side of a Hebrew sentence.
 *
 * Two details that are easy to get wrong:
 *
 * * Links the model wrote itself are stripped FIRST. Doing it afterwards would remove the
 *   entity links too, and leaving them lets the model put an arbitrary URL in a chat
 *   bubble — the vector that routing links through validated elements exists to close.
 * * Matching walks left to right and consumes each ref at most once, so an inserted link
 *   can never be matched into again. Without that, one label being a substring of another
 *   produces nested links and unrenderable markdown.
 */
export const injectEntityLinks = (
  text: string,
  elements?: TAiUiElement[],
): string => {
  const source = text.replace(MARKDOWN_LINK, "$1");
  const remaining = (elements ?? []).flatMap(element =>
    element.type === "entityRef" ? [element] : [],
  );
  if (!remaining.length) {
    return source;
  }

  let out = "";
  let cursor = 0;

  while (remaining.length) {
    let bestIndex = -1;
    let bestRef = -1;
    let bestLength = 0;
    remaining.forEach((candidate, i) => {
      const found = findLabel(source, candidate.label, cursor);
      if (!found) {
        return;
      }
      // Earliest match wins; on a tie the LONGER match wins. Without the tie-break,
      // "Ride 7" and "Ride 71" both match at the same offset and the shorter one is
      // consumed first, leaving a stray "1" outside the link.
      const better =
        bestIndex === -1 ||
        found.index < bestIndex ||
        (found.index === bestIndex && found.length > bestLength);
      if (better) {
        bestIndex = found.index;
        bestRef = i;
        bestLength = found.length;
      }
    });

    if (bestRef === -1) {
      break; // nothing left that actually appears in the text
    }

    const [ref] = remaining.splice(bestRef, 1);
    // Rewritten from the normalized LABEL, not the matched span: the span may carry the
    // line break the model wrapped the mention on, and re-emitting it would push the id
    // onto its own line (remark-breaks turns it into a <br>).
    const idSpan = findIdSpan(ref.label, ref.id);
    const target = `${ENTITY_SCHEME}${ref.entity}/${ref.id}`;

    out += source.slice(cursor, bestIndex);
    if (idSpan) {
      // The name is prose and stays prose; only the id becomes link text.
      out += ref.label.slice(0, idSpan.index);
      out += `[${escapeBrackets(idSpan.text)}](${target})`;
      out += ref.label.slice(idSpan.index + idSpan.length);
    } else {
      // No id in the text — link the normalized label rather than dropping the ref. A line
      // break inside link text does not parse as markdown, hence the label and not the span.
      out += `[${escapeBrackets(ref.label)}](${target})`;
    }
    // The matched span can be longer than the label it came from ("Ride\n7" vs "Ride 7"),
    // so the cursor advances by what was actually consumed.
    cursor = bestIndex + bestLength;
  }

  return out + source.slice(cursor);
};

export const setElementsOnLastAssistantMessage = (
  prev: IChatHistoryItem[],
  elements: TAiUiElement[],
): IChatHistoryItem[] => {
  const updated = [...prev];
  const lastMsg = updated[updated.length - 1];
  if (lastMsg?.role === AIRoleTypeEnum.Assistant) {
    updated[updated.length - 1] = { ...lastMsg, elements };
  }
  return updated;
};

export const setReasoningOnLastAssistantMessage = (
  prev: IChatHistoryItem[],
  reasoning: string,
): IChatHistoryItem[] => {
  const updated = [...prev];
  const lastMsg = updated[updated.length - 1];
  if (lastMsg?.role === AIRoleTypeEnum.Assistant) {
    updated[updated.length - 1] = { ...lastMsg, reasoning };
  }
  return updated;
};

export const addUserAndAssistantPlaceholder = (
  prev: IChatHistoryItem[],
  userText: string,
  userMessageIndex: number,
  assistantMessageIndex: number,
): IChatHistoryItem[] => [
  ...prev,
  {
    role: AIRoleTypeEnum.User,
    text: userText,
    messageIndex: userMessageIndex,
  },
  {
    role: AIRoleTypeEnum.Assistant,
    text: "",
    messageIndex: assistantMessageIndex,
  },
];

export interface IApplyFallbackResponseParams {
  success?: boolean;
  data?: { text?: string; sessionId?: string; reasoning?: string };
}

export const applyFallbackResponseToLastAssistant = (
  prev: IChatHistoryItem[],
  response: IApplyFallbackResponseParams | undefined,
  fallbackErrorText: string,
): IChatHistoryItem[] => {
  const updated = [...prev];
  const lastMsg = updated[updated.length - 1];
  if (lastMsg?.role !== AIRoleTypeEnum.Assistant) {
    return updated;
  }
  const { success, data } = response ?? {};
  if (success && data?.text) {
    updated[updated.length - 1] = {
      ...lastMsg,
      text: data.text,
      reasoning: data.reasoning,
    };
  } else if (!lastMsg.text) {
    updated[updated.length - 1] = {
      ...lastMsg,
      text: fallbackErrorText,
    };
  }
  return updated;
};

export const readSseStream = async (
  reader: ReadableStreamDefaultReader<Uint8Array>,
  callbacks: IAiStreamCallbacks,
): Promise<void> => {
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        break;
      }

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split("\n");
      buffer = lines.pop() ?? "";

      for (const line of lines) {
        if (!line.startsWith("data: ")) {
          continue;
        }
        const json = line.slice("data: ".length).trim();
        if (!json) {
          continue;
        }

        try {
          const event = JSON.parse(json) as TAiStreamEvent;
          switch (event.type) {
            case AiStreamEventEnum.Session:
              callbacks.onSession(event.sessionId);
              break;
            case AiStreamEventEnum.Chunk:
              callbacks.onChunk(event.text);
              break;
            case AiStreamEventEnum.Elements:
              callbacks.onElements?.(event.elements ?? []);
              break;
            case AiStreamEventEnum.Done:
              callbacks.onDone(event.reasoning);
              break;
            case AiStreamEventEnum.Error:
              callbacks.onError(event.message);
              break;
          }
        } catch {
          /* skip malformed SSE lines */
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
};
